import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useWwwRedirect } from "@/hooks/use-www-redirect";
import { useCanonicalUrl } from "@/hooks/use-canonical-url";

// Lazy load route components
const Index = lazy(() => import("./pages/Index"));
const Ventures = lazy(() => import("./pages/Ventures"));
const Studio = lazy(() => import("./pages/Studio"));
const Services = lazy(() => import("./pages/Services"));
const Model = lazy(() => import("./pages/Model"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Join = lazy(() => import("./pages/Join"));
const Partners = lazy(() => import("./pages/Partners"));
const Investors = lazy(() => import("./pages/Investors"));
const Press = lazy(() => import("./pages/Press"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Hub = lazy(() => import("./pages/Hub"));
const Launchpad = lazy(() => import("./pages/Launchpad"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VentureDetail = lazy(() => import("./pages/ventures/VentureDetail"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("./pages/case-studies/CaseStudyDetail"));
const Pitch = lazy(() => import("./pages/Pitch"));
const PostDetail = lazy(() => import("./pages/hub/PostDetail"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const UseCaseDetail = lazy(() => import("./pages/use-cases/UseCaseDetail"));
const CategoryHub = lazy(() => import("./pages/category/CategoryHub"));
const Directory = lazy(() => import("./pages/Directory"));
const Match = lazy(() => import("./pages/Match"));
const Team = lazy(() => import("./pages/Team"));
const Corridors = lazy(() => import("./pages/Corridors"));
const Government = lazy(() => import("./pages/Government"));
const BackTheStudio = lazy(() => import("./pages/BackTheStudio"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Component to handle redirects and canonical URLs inside Router context
const AppRoutes = () => {
  useWwwRedirect();
  useCanonicalUrl();

  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/ventures" element={<Ventures />} />
      <Route path="/ventures/:slug" element={<VentureDetail />} />
      <Route path="/studio" element={<Studio />} />
      <Route path="/services" element={<Services />} />
      <Route path="/model" element={<Model />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/hub" element={<Hub />} />
      <Route path="/hub/:id" element={<PostDetail />} />
      <Route path="/launchpad" element={<Launchpad />} />
      <Route path="/pitch" element={<Pitch />} />
      <Route path="/case-studies" element={<CaseStudies />} />
      <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/join" element={<Join />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/investors" element={<Investors />} />
      <Route path="/press" element={<Press />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/admin" element={<Dashboard />} />
      <Route path="/use-cases/:slug" element={<UseCaseDetail />} />
      <Route path="/category/:slug" element={<CategoryHub />} />
      <Route path="/directory" element={<Directory />} />
      <Route path="/match" element={<Match />} />
      <Route path="/team" element={<Team />} />
      <Route path="/corridors" element={<Corridors />} />
      <Route path="/partnerships" element={<Corridors />} />
      <Route path="/government" element={<Government />} />
      <Route path="/back-the-studio" element={<BackTheStudio />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <AppRoutes />
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
